import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import AppBadge from "../../components/ui/AppBadge";
import { TransactionListItem } from "../../models/BoricaTransaction";
import { boricaTransactionApiService } from "../../services/api/transactions/BoricaTransactionApiService";
import { toastService } from "../../services/toastService";
import { utils } from "../../utils/utils";

interface TransactionEditModalProps {
    transaction: TransactionListItem | null;
    visible: boolean;
    onHide: () => void;
    onSaved: () => void;
}

const statusOptions = [
    { label: "Successful", value: "Success" },
];

const isSuccess = (status: string) => status === "Success" || status === "2";

const TransactionEditModal: React.FC<TransactionEditModalProps> = ({
    transaction,
    visible,
    onHide,
    onSaved,
}) => {
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const editable = transaction != null && !isSuccess(transaction.status);

    const handleSave = async () => {
        if (!transaction || !selectedStatus) return;

        setIsSaving(true);
        try {
            const result = await boricaTransactionApiService.updateStatus({
                payDutyRequestId: transaction.payDutyRequestId,
                newStatus: selectedStatus,
            });

            if (result.success) {
                toastService.success("Transaction status updated successfully");
                if (result.data?.transactionStatusChanged) {
                    toastService.info("Payment status also updated to Successful");
                }
                setSelectedStatus(null);
                onSaved();
            } else {
                toastService.error(result.message || "Failed to update transaction status");
            }
        } catch {
            toastService.error("An error occurred while updating the transaction status");
        } finally {
            setIsSaving(false);
        }
    };

    const handleHide = () => {
        setSelectedStatus(null);
        onHide();
    };

    if (!transaction) return null;

    const footer = editable ? (
        <div className="flex justify-end gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={handleHide}
                className="p-button-text"
                disabled={isSaving}
            />
            <Button
                label="Save"
                icon="pi pi-check"
                onClick={handleSave}
                disabled={!selectedStatus || isSaving}
                loading={isSaving}
            />
        </div>
    ) : (
        <Button
            label="Close"
            icon="pi pi-times"
            onClick={handleHide}
            className="p-button-text"
        />
    );

    return (
        <Dialog
            header="Transaction Details"
            visible={visible}
            style={{ width: "550px" }}
            contentStyle={{ maxHeight: "70vh", overflowY: "auto", position: "relative" }}
            modal
            onHide={handleHide}
            footer={footer}
        >
            <div className="flex flex-col gap-3">
                <SectionHeader title="User Information" />
                <DetailRow label="First Name" value={transaction.firstName} />
                <DetailRow label="Last Name" value={transaction.lastName} />
                <DetailRow label="Email" value={transaction.email} />
                <DetailRow label="Phone Number" value={transaction.phoneNumber} />

                <SectionHeader title="Payment Details" />
                <DetailRow label="Service" value={transaction.service} />
                <DetailRow label="Account Number" value={transaction.accountNumber} />
                <DetailRow label="Duty Amount" value={fmt(transaction.amount, transaction.currency)} />
                <DetailRow label="Total Transaction Amount" value={fmt(transaction.transactionAmount, transaction.currency)} />
                <DetailRow label="Description" value={transaction.description} />
                <DetailRow label="Order ID" value={transaction.orderId} />

                <SectionHeader title="Identifiers" />
                <DetailRow label="Pay Duty Request ID" value={str(transaction.payDutyRequestId)} />
                <DetailRow label="Borica Transaction ID" value={str(transaction.boricaTransactionId)} />
                <DetailRow label="Request ID" value={transaction.requestId} />
                <DetailRow label="Approval Code" value={transaction.callbackApproval} />
                <DetailRow label="RRN" value={transaction.callbackRrn} />
                <DetailRow label="FastPay Payment IDs" value={transaction.fastPayPaymentIds} />

                <SectionHeader title="Dates" />
                <DetailRow label="Created At" value={fmtDate(transaction.createdAt)} />
                <DetailRow label="Updated At" value={fmtDate(transaction.updatedAt)} />
                <DetailRow label="Transaction Date" value={fmtDate(transaction.transactionCreatedAt)} />

                {transaction.errorMessage && (
                    <>
                        <SectionHeader title="Error" />
                        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">{transaction.errorMessage}</div>
                    </>
                )}

                <SectionHeader title="Status" />
                <DetailRow label="Current Status" value={transaction.status} />
                {editable ? (
                    <div className="flex items-center gap-3">
                        <span className="font-semibold text-sm w-40 shrink-0">New Status</span>
                        <Dropdown
                            value={selectedStatus}
                            options={statusOptions}
                            onChange={(e) => setSelectedStatus(e.value)}
                            placeholder="Select new status"
                            className="w-full"
                        />
                    </div>
                ) : (
                    <div className="flex items-center gap-3">
                        <span className="font-semibold text-sm w-40 shrink-0" />
                        <AppBadge variant="success">No changes allowed</AppBadge>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
    <div className="font-semibold text-sm text-neutral-500 border-b border-neutral-200 pb-1 mt-2">
        {title}
    </div>
);

const str = (val: unknown): string => {
    if (val === null || val === undefined) return "";
    return String(val);
};

const fmt = (amount: unknown, currency: unknown): string => {
    if (amount === null || amount === undefined) return "-";
    const c = currency ? ` ${currency}` : "";
    return `${amount}${c}`;
};

const fmtDate = (val: unknown): string => {
    if (!val) return "-";
    return utils.formatDateTimeSofia(val as string);
};

const DetailRow: React.FC<{ label: string; value: unknown }> = ({ label, value }) => (
    <div className="flex items-center gap-3">
        <span className="font-semibold text-sm w-40 shrink-0">{label}</span>
        <span className="text-sm">{str(value) || "-"}</span>
    </div>
);

export default TransactionEditModal;
